import type {
  AllergyIntolerance,
  Appointment,
  AuditEvent,
  Condition,
  Consent,
  DiagnosticReport,
  DocumentReference,
  Encounter,
  FamilyMemberHistory,
  Immunization,
  MedicationRequest,
  MedicationStatement,
  Observation,
  Organization,
  Patient,
  Procedure,
  Provenance,
  RequestGroup,
  ServiceRequest,
} from '@medplum/fhirtypes'

type Config = {
  defaultFields: string[]
  availableFields: { code: string; title: string }[]
  availableSorting: string[]
  sortMapping?: Record<string, string>
}

export type AvailResource =
  | Patient
  | Observation
  | FamilyMemberHistory
  | Procedure
  | Condition
  | MedicationStatement
  // DoseSpot-synced prescriptions, which the Medications section renders alongside the
  // note-authored `MedicationStatement`s (§10.6). They are fetched by
  // `useDoseSpotPrescriptions`, not by `useFhirSearch` — the table unions them in — so
  // the config entry below exists only to satisfy the exhaustive `fhirSearchConfigs`.
  | MedicationRequest
  | AllergyIntolerance
  | Immunization
  | DiagnosticReport
  | DocumentReference
  | Provenance
  | ServiceRequest
  | RequestGroup
  | Encounter
  | Organization
  | Consent
  | AuditEvent
  | Appointment

export type AvailResourceType = AvailResource['resourceType']

export const fhirSearchConfigs: Record<AvailResourceType, Config> = {
  Patient: {
    defaultFields: [
      'photo',
      'name',
      'birthdate',
      'phone',
      'address-state',
      'address-city',
      'address-postalcode',
    ],
    availableFields: [
      { code: 'photo', title: 'Photo' },
      { code: 'name', title: 'Name' },
      { code: 'gender', title: 'Gender' },
      { code: 'birthdate', title: 'Birth Date' },
      { code: 'phone', title: 'Phone' },
      { code: 'email', title: 'Email' },
      { code: 'address-state', title: 'State' },
      { code: 'address-city', title: 'City' },
      { code: 'address-line', title: 'Address' },
      { code: 'address-postalcode', title: 'Postal Code' },
    ],
    availableSorting: [
      'name',
      'birthdate',
      'phone',
      'address-state',
      'address-postalcode',
      'email',
      '_lastUpdated',
    ],
    sortMapping: { name: 'family' },
  },
  Observation: {
    defaultFields: ['date', 'performer', 'result'],
    availableFields: [
      { code: 'date', title: 'Date' },
      { code: 'performer', title: 'Performer' },
      { code: 'result', title: 'Result' },
    ],
    availableSorting: ['date', 'category', 'performer', '_lastUpdated'],
  },
  FamilyMemberHistory: {
    defaultFields: ['relationship', 'name', 'date'],
    availableFields: [
      { code: 'relationship', title: 'Relationship' },
      { code: 'name', title: 'Name' },
      { code: 'date', title: 'Date' },
      { code: 'condition', title: 'Condition' },
      { code: 'status', title: 'Status' },
    ],
    availableSorting: ['date', 'status', '_lastUpdated'],
  },
  Procedure: {
    defaultFields: ['code', 'status', 'performedDateTime', 'performer', 'location'],
    availableFields: [
      { code: 'code', title: 'Code' },
      { code: 'status', title: 'Status' },
      { code: 'performedDateTime', title: 'Performed Date' },
      { code: 'performer', title: 'Performer' },
      { code: 'location', title: 'Location' },
      { code: 'category', title: 'Category' },
      { code: 'reasonCode', title: 'Reason' },
    ],
    availableSorting: ['date', 'status', 'code', '_lastUpdated'],
  },
  Condition: {
    defaultFields: ['code', 'clinicalStatus', 'recordedDate', 'recorder'],
    availableFields: [
      { code: 'code', title: 'Code' },
      { code: 'clinicalStatus', title: 'Clinical Status' },
      { code: 'verificationStatus', title: 'Verification Status' },
      { code: 'severity', title: 'Severity' },
      { code: 'recordedDate', title: 'Recorded Date' },
      { code: 'recorder', title: 'Recorder' },
      { code: 'category', title: 'Category' },
      { code: 'note', title: 'Note' },
    ],
    availableSorting: [
      'recorded-date',
      'status',
      'onset-date',
      'clinicalStatus',
      'clinical-status',
      'code',
      '_lastUpdated',
    ],
  },
  MedicationStatement: {
    defaultFields: ['status', 'medication', 'effectiveDateTime', 'dosage'],
    availableFields: [
      { code: 'status', title: 'Status' },
      { code: 'medication', title: 'Medication' },
      { code: 'effectiveDateTime', title: 'Effective Date' },
      { code: 'dosage', title: 'Dosage' },
    ],
    availableSorting: ['status', 'effective', '_lastUpdated'],
  },
  // Empty on purpose: no chart section searches `MedicationRequest` through
  // `useFhirSearch`, so nothing reads this entry. It exists because the record is
  // exhaustive over `AvailResource`, and inventing fields would imply a search that
  // does not happen. See the union member above.
  MedicationRequest: {
    defaultFields: [],
    availableFields: [],
    availableSorting: [],
  },
  AllergyIntolerance: {
    defaultFields: ['code', 'clinicalStatus', 'verificationStatus', 'recordedDate'],
    availableFields: [
      { code: 'code', title: 'Allergen' },
      { code: 'clinicalStatus', title: 'Clinical Status' },
      { code: 'verificationStatus', title: 'Verification Status' },
      { code: 'type', title: 'Type' },
      { code: 'category', title: 'Category' },
      { code: 'criticality', title: 'Criticality' },
      { code: 'recordedDate', title: 'Recorded Date' },
      { code: 'recorder', title: 'Recorder' },
    ],
    availableSorting: ['date', 'code', 'clinical-status', 'verification-status', '_lastUpdated'],
  },
  Immunization: {
    defaultFields: ['status', 'vaccineCode', 'occurrence', 'performer', 'location'],
    availableFields: [
      { code: 'status', title: 'Status' },
      { code: 'vaccineCode', title: 'Vaccine' },
      { code: 'occurrence', title: 'Date' },
      { code: 'performer', title: 'Performer' },
      { code: 'location', title: 'Location' },
      { code: 'lotNumber', title: 'Lot Number' },
      { code: 'route', title: 'Route' },
    ],
    availableSorting: ['date', 'status', '_lastUpdated'],
  },
  DiagnosticReport: {
    defaultFields: ['status', 'code', 'effectiveDateTime', 'performer', 'result'],
    availableFields: [
      { code: 'status', title: 'Status' },
      { code: 'code', title: 'Code' },
      { code: 'effectiveDateTime', title: 'Effective Date' },
      { code: 'performer', title: 'Performer' },
      { code: 'result', title: 'Result' },
      { code: 'category', title: 'Category' },
      { code: 'conclusion', title: 'Conclusion' },
    ],
    availableSorting: ['date', 'status', 'code', 'category', '_lastUpdated'],
  },
  DocumentReference: {
    defaultFields: ['status', 'type', 'date', 'author'],
    availableFields: [
      { code: 'status', title: 'Status' },
      { code: 'type', title: 'Type' },
      { code: 'date', title: 'Date' },
      { code: 'author', title: 'Author' },
    ],
    availableSorting: ['date', 'status', '_lastUpdated'],
  },
  Provenance: {
    defaultFields: ['recorded', 'agent', 'activity'],
    availableFields: [
      { code: 'recorded', title: 'Recorded' },
      { code: 'agent', title: 'Agent' },
      { code: 'activity', title: 'Activity' },
    ],
    availableSorting: ['recorded', '_lastUpdated'],
  },
  ServiceRequest: {
    defaultFields: ['status', 'intent', 'code', 'authoredOn', 'requester'],
    availableFields: [
      { code: 'status', title: 'Status' },
      { code: 'intent', title: 'Intent' },
      { code: 'code', title: 'Code' },
      { code: 'authoredOn', title: 'Authored Date' },
      { code: 'requester', title: 'Requester' },
    ],
    availableSorting: ['status', 'intent', 'authoredOn', '_lastUpdated'],
  },
  RequestGroup: {
    defaultFields: ['status', 'intent', 'priority', 'authoredOn'],
    availableFields: [
      { code: 'status', title: 'Status' },
      { code: 'intent', title: 'Intent' },
      { code: 'priority', title: 'Priority' },
      { code: 'authoredOn', title: 'Authored Date' },
    ],
    availableSorting: ['status', 'priority', 'authoredOn', '_lastUpdated'],
  },
  Encounter: {
    defaultFields: ['status', 'type', 'period', 'location', 'participant'],
    availableFields: [
      { code: 'status', title: 'Status' },
      { code: 'type', title: 'Type' },
      { code: 'period', title: 'Period' },
      { code: 'location', title: 'Location' },
      { code: 'participant', title: 'Participant' },
      { code: 'class', title: 'Class' },
      { code: 'serviceType', title: 'Service Type' },
    ],
    availableSorting: ['date', 'status', 'type'],
  },
  Organization: {
    defaultFields: ['name', 'type', 'address'],
    availableFields: [
      { code: 'name', title: 'Name' },
      { code: 'type', title: 'Type' },
      { code: 'address', title: 'Address' },
      { code: 'contact', title: 'Contact' },
      { code: 'identifier', title: 'Identifier' },
      { code: 'active', title: 'Active' },
      { code: 'partOf', title: 'Part Of' },
    ],
    availableSorting: ['name', 'type', '_lastUpdated'],
  },
  Consent: {
    defaultFields: ['status', 'scope', 'category', 'date', 'patient', 'performer', 'organization'],
    availableFields: [
      { code: 'status', title: 'Status' },
      { code: 'scope', title: 'Scope' },
      { code: 'category', title: 'Category' },
      { code: 'date', title: 'Date' },
      { code: 'patient', title: 'Patient' },
      { code: 'performer', title: 'Performer' },
      { code: 'organization', title: 'Organization' },
      { code: 'policy', title: 'Policy' },
    ],
    availableSorting: ['status', 'date', 'category', '_lastUpdated', 'patient'],
  },
  AuditEvent: {
    defaultFields: ['recorded', 'type', 'agent', 'source', 'entity'],
    availableFields: [
      { code: 'recorded', title: 'Recorded Date' },
      { code: 'type', title: 'Type' },
      { code: 'agent', title: 'Agent' },
      { code: 'source', title: 'Source' },
      { code: 'entity', title: 'Entity' },
      { code: 'outcome', title: 'Outcome' },
    ],
    availableSorting: ['date', 'type', '_lastUpdated'],
  },
  Appointment: {
    defaultFields: ['status', 'start', 'participant'],
    availableFields: [
      { code: 'status', title: 'Status' },
      { code: 'start', title: 'Start Date' },
      { code: 'participant', title: 'Participant' },
      { code: 'serviceType', title: 'Service Type' },
      { code: 'appointmentType', title: 'Appointment Type' },
    ],
    availableSorting: ['date', 'status', '_lastUpdated', 'patient'],
  },
}
